import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, CreditCard, Building2, Printer } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { apiClient } from '../../utils/apiHelper';
import { formatDate } from '../../utils/dateTimeUtils';

const HostelFine = () => {
  const [fines, setFines] = useState([]);
  const [hostels, setHostels] = useState([]);
  const [allStudents, setAllStudents] = useState([]);  // Main student database
  const [banks, setBanks] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPayModalOpen, setIsPayModalOpen] = useState(false);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [selectedFine, setSelectedFine] = useState(null);
  const [printFine, setPrintFine] = useState(null);
  const [loading, setLoading] = useState(false);
  const fineReceiptRef = useRef();
  
  const [formData, setFormData] = useState({
    fine_number: `FINE-${Date.now()}`,
    student_id: '',
    student_name: '',
    hostel_id: '',
    hostel_name: '',
    fine_date: new Date().toISOString().split('T')[0],
    fine_reason: '',
    fine_amount: 0,
    remarks: '',
    issued_by: 'Admin',
    paid_amount: 0,
    payment_status: 'Unpaid'
  });

  const [paymentData, setPaymentData] = useState({
    amount: 0,
    payment_mode: 'Cash',
    bank_id: '',
    bank_name: '',
    payment_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchFines();
    fetchHostels();
    fetchAllStudents();  // Fetch from main student database
    fetchBanks();
  }, []);

  const fetchFines = async () => {
    try {
      const response = await apiClient.get('/api/hostel/fines');
      setFines(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchHostels = async () => {
    try {
      const response = await apiClient.get('/api/hostel/hostels');
      setHostels(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchAllStudents = async () => {
    try {
      // Fetch from main student database
      const response = await apiClient.get('/api/students/dropdown');
      setAllStudents(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };

  const fetchBanks = async () => {
    try {
      const response = await apiClient.get('/api/master-settings/banks');
      setBanks(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching banks:', error);
      setBanks([]);
    }
  };

  const handleStudentChange = (studentId) => {
    // Use main student database (allStudents)
    const student = allStudents.find(s => s.id === studentId);
    if (student) {
      setFormData({
        ...formData,
        student_id: studentId,
        student_name: student.full_name,  // Main DB uses full_name
        // Don't auto-fill hostel - let user select
      });
    }
  };

  const handleHostelChange = (hostelId) => {
    const hostel = hostels.find(h => h.id === hostelId);
    if (hostel) {
      setFormData({ ...formData, hostel_id: hostelId, hostel_name: hostel.hostel_name });
    }
  };

  const handleBankChange = (bankId) => {
    const bank = banks.find(b => b.id === bankId);
    if (bank) {
      setPaymentData({ ...paymentData, bank_id: bankId, bank_name: bank.bank_name });
    }
  };

  const isBankPayment = paymentData.payment_mode !== 'Cash';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await apiClient.post('/api/hostel/fines', formData);
      if (response.status === 200 || response.data) {
        alert('Fine issued!');
        fetchFines();
        handleCloseModal();
      }
    } catch (error) {
      alert('Failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePayFine = async (e) => {
    e.preventDefault();
    
    if (!selectedFine) return;
    
    if (isBankPayment && !paymentData.bank_id) {
      alert('Please select a bank for non-cash payment');
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams({
        amount: paymentData.amount.toString(),
        payment_mode: paymentData.payment_mode,
        payment_date: paymentData.payment_date
      });
      
      if (paymentData.bank_id) {
        params.append('bank_id', paymentData.bank_id);
        params.append('bank_name', paymentData.bank_name);
      }

      const response = await apiClient.put(`/api/hostel/fines/${selectedFine.id}/pay?${params.toString()}`);
      
      if (response.status === 200 || response.data) {
        const registerType = paymentData.payment_mode === 'Cash' ? 'Cash Register' : 'Bank Register';
        alert(`Fine payment recorded! Entry added to ${registerType}`);
        fetchFines();
        fetchBanks();
        handleClosePayModal();
      }
    } catch (error) {
      console.error('Payment error:', error);
      alert('Failed to record payment');
    } finally {
      setLoading(false);
    }
  };

  const openPayModal = (fine) => {
    setSelectedFine(fine);
    const pendingAmount = fine.fine_amount - fine.paid_amount;
    setPaymentData({
      amount: pendingAmount,
      payment_mode: 'Cash',
      bank_id: '',
      bank_name: '',
      payment_date: new Date().toISOString().split('T')[0]
    });
    setIsPayModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setFormData({
      fine_number: `FINE-${Date.now()}`,
      student_id: '',
      student_name: '',
      hostel_id: '',
      hostel_name: '',
      fine_date: new Date().toISOString().split('T')[0],
      fine_reason: '',
      fine_amount: 0,
      remarks: '',
      issued_by: 'Admin',
      paid_amount: 0,
      payment_status: 'Unpaid'
    });
  };

  const handleClosePayModal = () => {
    setIsPayModalOpen(false);
    setSelectedFine(null);
    setPaymentData({
      amount: 0,
      payment_mode: 'Cash',
      bank_id: '',
      bank_name: '',
      payment_date: new Date().toISOString().split('T')[0]
    });
  };

  const handlePrintFine = () => {
    const printContent = fineReceiptRef.current;
    if (!printContent) return;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Fine Receipt - ${printFine?.fine_number || ''}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            .text-center { text-align: center; }
            .border-b { border-bottom: 1px solid #ddd; padding-bottom: 15px; margin-bottom: 15px; }
            .font-bold { font-weight: bold; }
            .text-xl { font-size: 1.25rem; }
            .text-sm { font-size: 0.875rem; }
            .text-xs { font-size: 0.75rem; }
            .text-gray-500 { color: #6b7280; }
            .text-gray-600 { color: #4b5563; }
            .text-gray-400 { color: #9ca3af; }
            .text-green-600 { color: #16a34a; }
            .text-red-600 { color: #dc2626; }
            .space-y-3 > * + * { margin-top: 0.75rem; }
            .flex { display: flex; }
            .justify-between { justify-content: space-between; }
            .border-t { border-top: 1px solid #ddd; padding-top: 15px; margin-top: 15px; }
            .text-right { text-align: right; }
            .max-w-200 { max-width: 200px; }
            .font-medium { font-weight: 500; }
            .font-semibold { font-weight: 600; }
            .mt-2 { margin-top: 0.5rem; }
            .pt-2 { padding-top: 0.5rem; }
            .mt-6 { margin-top: 1.5rem; }
            .pt-4 { padding-top: 1rem; }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const openPrintModal = (fine) => {
    setPrintFine(fine);
    setIsPrintModalOpen(true);
  };

  return (
    <div className="p-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Hostel Fines</CardTitle>
          <Button onClick={() => setIsModalOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />Issue Fine
          </Button>
        </CardHeader>
        <CardContent>
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left p-2">Fine #</th>
                <th className="text-left p-2">Date</th>
                <th className="text-left p-2">Student</th>
                <th className="text-left p-2">Reason</th>
                <th className="text-right p-2">Amount</th>
                <th className="text-right p-2">Paid</th>
                <th className="text-left p-2">Status</th>
                <th className="text-center p-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {fines.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center p-4 text-gray-500">No fines issued.</td>
                </tr>
              ) : (
                fines.map((fine) => (
                  <tr key={fine.id} className="border-b hover:bg-gray-50">
                    <td className="p-2 font-medium">{fine.fine_number}</td>
                    <td className="p-2">{formatDate(fine.fine_date)}</td>
                    <td className="p-2">{fine.student_name}</td>
                    <td className="p-2">{fine.fine_reason}</td>
                    <td className="p-2 text-right">₹{fine.fine_amount}</td>
                    <td className="p-2 text-right text-green-600">₹{fine.paid_amount || 0}</td>
                    <td className="p-2">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        fine.payment_status === 'Paid' ? 'bg-green-100 text-green-800' : 
                        fine.payment_status === 'Partial' ? 'bg-yellow-100 text-yellow-800' : 
                        'bg-red-100 text-red-800'
                      }`}>
                        {fine.payment_status || 'Unpaid'}
                      </span>
                    </td>
                    <td className="p-2 text-center">
                      <div className="flex gap-1 justify-center">
                        {fine.payment_status !== 'Paid' && (
                          <Button size="sm" variant="outline" onClick={() => openPayModal(fine)}>
                            <CreditCard className="w-4 h-4 mr-1" />Pay
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => openPrintModal(fine)}>
                          <Printer className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Issue Fine Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Issue Fine</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Fine Number</Label>
                <Input value={formData.fine_number} disabled />
              </div>
              <div>
                <Label>Fine Date *</Label>
                <Input type="date" required value={formData.fine_date} onChange={(e) => setFormData({ ...formData, fine_date: e.target.value })} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Select Student *</Label>
                <Select value={formData.student_id} onValueChange={handleStudentChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="-- Select Student --" />
                  </SelectTrigger>
                  <SelectContent>
                    {allStudents.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.full_name} ({s.admission_number || 'N/A'})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Hostel</Label>
                <Select value={formData.hostel_id} onValueChange={handleHostelChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select hostel" />
                  </SelectTrigger>
                  <SelectContent>
                    {hostels.map((h) => (
                      <SelectItem key={h.id} value={h.id}>{h.hostel_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Fine Reason *</Label>
              <Input required value={formData.fine_reason} onChange={(e) => setFormData({ ...formData, fine_reason: e.target.value })} placeholder="e.g., Noise complaint, Damage to property" />
            </div>

            <div>
              <Label>Fine Amount (₹) *</Label>
              <Input type="number" step="0.01" required value={formData.fine_amount} onChange={(e) => setFormData({ ...formData, fine_amount: parseFloat(e.target.value) || 0 })} />
            </div>

            <div>
              <Label>Remarks</Label>
              <Input value={formData.remarks} onChange={(e) => setFormData({ ...formData, remarks: e.target.value })} />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseModal}>Cancel</Button>
              <Button type="submit" disabled={loading}>{loading ? 'Saving...' : 'Issue Fine'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Pay Fine Modal */}
      <Dialog open={isPayModalOpen} onOpenChange={setIsPayModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Pay Fine</DialogTitle>
          </DialogHeader>
          {selectedFine && (
            <form onSubmit={handlePayFine} className="space-y-4">
              <div className="bg-gray-50 p-3 rounded-lg text-sm">
                <p><strong>Fine #:</strong> {selectedFine.fine_number}</p>
                <p><strong>Student:</strong> {selectedFine.student_name}</p>
                <p><strong>Reason:</strong> {selectedFine.fine_reason}</p>
                <p><strong>Total Fine:</strong> ₹{selectedFine.fine_amount}</p>
                <p><strong>Already Paid:</strong> ₹{selectedFine.paid_amount || 0}</p>
                <p className="text-red-600 font-medium"><strong>Pending:</strong> ₹{selectedFine.fine_amount - (selectedFine.paid_amount || 0)}</p>
              </div>

              <div>
                <Label>Payment Date *</Label>
                <Input type="date" value={paymentData.payment_date} onChange={(e) => setPaymentData({ ...paymentData, payment_date: e.target.value })} max={new Date().toISOString().split('T')[0]} data-testid="fine-payment-date" />
              </div>

              <div>
                <Label>Payment Amount (₹) *</Label>
                <Input 
                  type="number" 
                  step="0.01" 
                  required 
                  value={paymentData.amount} 
                  onChange={(e) => setPaymentData({ ...paymentData, amount: parseFloat(e.target.value) || 0 })} 
                  max={selectedFine.fine_amount - (selectedFine.paid_amount || 0)}
                />
              </div>

              <div>
                <Label>Payment Mode *</Label>
                <Select value={paymentData.payment_mode} onValueChange={(value) => setPaymentData({ ...paymentData, payment_mode: value, bank_id: '', bank_name: '' })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Cash">Cash</SelectItem>
                    <SelectItem value="Bank">Bank Transfer</SelectItem>
                    <SelectItem value="Cheque">Cheque</SelectItem>
                    <SelectItem value="UPI">UPI</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {isBankPayment && (
                <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                  <Label className="flex items-center gap-2 text-blue-800 mb-2">
                    <Building2 className="w-4 h-4" />
                    Select Bank *
                  </Label>
                  <Select value={paymentData.bank_id} onValueChange={handleBankChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="-- Select Bank --" />
                    </SelectTrigger>
                    <SelectContent>
                      {banks.map((bank) => (
                        <SelectItem key={bank.id} value={bank.id}>
                          {bank.bank_name} (Balance: ₹{bank.current_balance?.toLocaleString('en-IN') || 0})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleClosePayModal}>Cancel</Button>
                <Button type="submit" disabled={loading}>{loading ? 'Processing...' : 'Record Payment'}</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Print Fine Receipt Modal */}
      <Dialog open={isPrintModalOpen} onOpenChange={setIsPrintModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Fine Receipt</DialogTitle>
          </DialogHeader>
          
          <div ref={fineReceiptRef} className="p-6 bg-white">
            <div className="text-center border-b pb-4 mb-4">
              <h2 className="text-xl font-bold">HOSTEL FINE RECEIPT</h2>
              <p className="text-sm text-gray-500">{printFine?.payment_status === 'Paid' ? 'Payment Confirmation' : 'Fine Notice'}</p>
            </div>
            
            {printFine && (
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Fine No:</span>
                  <span className="font-medium">{printFine.fine_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Date:</span>
                  <span>{formatDate(printFine.fine_date)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Student:</span>
                  <span>{printFine.student_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Hostel:</span>
                  <span>{printFine.hostel_name || 'N/A'}</span>
                </div>
                <div className="border-t pt-3 mt-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Reason:</span>
                    <span className="text-right max-w-[200px]">{printFine.fine_reason}</span>
                  </div>
                </div>
                {printFine.remarks && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Remarks:</span>
                    <span>{printFine.remarks}</span>
                  </div>
                )}
                <div className="border-t pt-3 mt-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Fine Amount:</span>
                    <span className="font-semibold text-red-600">₹{printFine.fine_amount?.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Paid Amount:</span>
                    <span className="text-green-600">₹{(printFine.paid_amount || 0)?.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between font-bold mt-2 pt-2 border-t">
                    <span>Balance Due:</span>
                    <span className={printFine.payment_status === 'Paid' ? 'text-green-600' : 'text-red-600'}>
                      ₹{(printFine.fine_amount - (printFine.paid_amount || 0))?.toLocaleString('en-IN')}
                    </span>
                  </div>
                </div>
                {printFine.payment_mode && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Payment Mode:</span>
                    <span>{printFine.payment_mode}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <span className={`font-semibold ${printFine.payment_status === 'Paid' ? 'text-green-600' : 'text-red-600'}`}>
                    {printFine.payment_status || 'Unpaid'}
                  </span>
                </div>
                <div className="text-center text-xs text-gray-400 mt-6 pt-4 border-t">
                  <p>Issued by: {printFine.issued_by || 'Admin'}</p>
                  <p>This is a computer generated receipt</p>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsPrintModalOpen(false)}>
              Close
            </Button>
            <Button onClick={handlePrintFine}>
              <Printer className="w-4 h-4 mr-2" />Print
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HostelFine;
